import { useEffect, useState } from 'react';
import {
  X, GitBranch, ExternalLink, Globe, GitPullRequest, ShieldCheck, Trash2,
} from 'lucide-react';
import { Spinner } from '@systemdox/ui';
import {
  specsApi, type PublishResult, type PublishMode, type SpecDetail,
} from '@/services/specsApi';
import { githubApi } from '@/services/githubApi';
import { sanitizeApi, type SanitizeTestResult } from '@/services/sanitizeApi';
import { cn } from '@/lib/utils';

interface PublishDialogProps {
  draftId: string;
  specName: string;
  defaultRepo: string;
  /** Settings ▸ Repository roles URL — shown when no repo could be defaulted. */
  configureHref?: string;
  onClose: () => void;
  onPublished: (result: PublishResult) => void;
  /** Fired after a successful unpublish so callers can refresh state. */
  onUnpublished?: () => void;
}

/** One markdown document for the sanitize preview — same composition the
 *  publish commit ships (proposal + requirements + tasks). */
function composeForScan(detail: SpecDetail): string {
  const parts = [detail.proposalContent];
  for (const req of detail.requirements) parts.push(`## ${req.name}\n\n${req.content}`);
  parts.push(detail.taskContent);
  return parts.filter(Boolean).join('\n\n');
}

/**
 * Publish to the Knowledge Base (kb-publish R1/R2/R5): the default mode
 * commits to the repo's default branch and the KB picks it up once indexed;
 * "Propose via pull request" keeps the branch+PR flow. The dialog previews
 * what the sanitizer will redact before the author confirms, and hosts
 * Unpublish for already-published documents (R7).
 */
export function PublishDialog({
  draftId, specName, defaultRepo, configureHref, onClose, onPublished, onUnpublished,
}: PublishDialogProps) {
  const [repo, setRepo] = useState(defaultRepo);
  const [mode, setMode] = useState<PublishMode>('publish');
  const [branch, setBranch] = useState(`spec/${specName.toLowerCase().replace(/\s+/g, '-')}`);
  const [createPr, setCreatePr] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [result, setResult] = useState<PublishResult | null>(null);
  const [error, setError] = useState('');

  // The document itself — content for the scan preview, publishedAt for the
  // Unpublish affordance.
  const [detail, setDetail] = useState<SpecDetail | null>(null);
  const [scan, setScan] = useState<SanitizeTestResult | null>(null);
  const [scanState, setScanState] = useState<'loading' | 'done' | 'unavailable'>('loading');
  const [showFindings, setShowFindings] = useState(false);
  const [unpublishing, setUnpublishing] = useState(false);
  const [unpublished, setUnpublished] = useState(false);

  // No spec repo and no configured pillar repo: if exactly one repo is
  // connected (the whole free tier), suggest it instead of an empty field the
  // user has to fill from memory. Best-effort — a failed read just leaves the
  // field blank as before.
  useEffect(() => {
    if (defaultRepo) return;
    let cancelled = false;
    githubApi
      .listRepos()
      .then((list) => {
        const connected = list.filter((r) => r.connected);
        if (!cancelled && connected.length === 1) {
          setRepo((prev) => prev || connected[0].full_name);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [defaultRepo]);

  // Sanitize preview (R5): run the dry-run scanner on the composed document.
  // The KB build sanitizes regardless — this only shows the author what will
  // be redacted. The tester endpoint may be admin-gated; fail quiet.
  useEffect(() => {
    let cancelled = false;
    specsApi
      .getSpec(draftId)
      .then(async (d) => {
        if (cancelled) return;
        setDetail(d);
        try {
          const test = await sanitizeApi.testPolicy(composeForScan(d));
          if (!cancelled) {
            setScan(test);
            setScanState('done');
          }
        } catch {
          if (!cancelled) setScanState('unavailable');
        }
      })
      .catch(() => {
        if (!cancelled) setScanState('unavailable');
      });
    return () => {
      cancelled = true;
    };
  }, [draftId]);

  async function handlePublish() {
    setPublishing(true);
    setError('');
    try {
      const res = await specsApi.publishSpec(draftId, mode === 'publish'
        ? { repoFullName: repo, mode }
        : { repoFullName: repo, branch, createPr, mode });
      setResult(res);
      onPublished(res);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Publish failed');
    } finally {
      setPublishing(false);
    }
  }

  async function handleUnpublish() {
    if (!window.confirm(
      'Unpublish this document? A removal commit deletes its folder from the repository, '
      + 'and the Knowledge Base drops the document once the removal is indexed.',
    )) return;
    setUnpublishing(true);
    setError('');
    try {
      await specsApi.unpublishSpec(draftId);
      setUnpublished(true);
      onUnpublished?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unpublish failed');
    } finally {
      setUnpublishing(false);
    }
  }

  const redactions = scan?.counts.redacted ?? 0;
  const reported = scan?.counts.reported ?? 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="fixed inset-0 bg-black/50" />
      <div
        className="relative w-full max-w-md max-h-[90vh] overflow-y-auto bg-white dark:bg-slate-900 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700 p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <button onClick={onClose} className="absolute top-4 right-4 text-slate-400 hover:text-slate-600">
          <X className="w-4 h-4" />
        </button>

        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">
          Publish to the Knowledge Base
        </h2>

        {unpublished ? (
          <div className="space-y-3">
            <p className="text-sm text-slate-700 dark:text-slate-300 font-medium">
              Unpublished. The removal commit is in the repository and the Knowledge Base drops the
              document once the removal is indexed.
            </p>
            <button
              onClick={onClose}
              className="w-full mt-2 px-3 py-2 rounded-md bg-slate-100 dark:bg-slate-800 text-sm font-medium"
            >
              Close
            </button>
          </div>
        ) : result ? (
          <div className="space-y-3">
            {result.mode === 'propose' ? (
              <>
                <p className="text-sm text-emerald-600 dark:text-emerald-400 font-medium">
                  {result.filesCommitted} files committed to {result.repoFullName} on {result.branch}
                </p>
                {result.prUrl && (
                  <a
                    href={result.prUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    View Pull Request
                  </a>
                )}
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  The Knowledge Base updates automatically when the pull request merges.
                </p>
              </>
            ) : (
              <>
                <p className="text-sm text-emerald-600 dark:text-emerald-400 font-medium">
                  Published — {result.filesCommitted} files committed to {result.repoFullName}
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  The document is committed to GitHub; it appears in the Knowledge Base once indexed.
                  {redactions > 0 && ` ${redactions} redaction${redactions === 1 ? '' : 's'} will apply to the published pages.`}
                </p>
              </>
            )}
            <button
              onClick={onClose}
              className="w-full mt-4 px-3 py-2 rounded-md bg-slate-100 dark:bg-slate-800 text-sm font-medium"
            >
              Close
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {error && (
              <div className="p-3 rounded-md bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm">
                {error}
              </div>
            )}

            {/* Mode (R1): straight to the KB, or a reviewed proposal. */}
            <div className="grid grid-cols-1 gap-2">
              <button
                onClick={() => setMode('publish')}
                className={cn(
                  'flex items-start gap-2.5 p-3 rounded-lg border text-left transition-colors',
                  mode === 'publish'
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600',
                )}
              >
                <Globe className="w-4 h-4 mt-0.5 shrink-0 text-blue-600 dark:text-blue-400" />
                <span>
                  <span className="block text-sm font-medium text-slate-900 dark:text-slate-100">
                    Publish to the Knowledge Base
                  </span>
                  <span className="block text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                    Commits to the repository's default branch; the Knowledge Base picks it up once indexed.
                  </span>
                </span>
              </button>
              <button
                onClick={() => setMode('propose')}
                className={cn(
                  'flex items-start gap-2.5 p-3 rounded-lg border text-left transition-colors',
                  mode === 'propose'
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600',
                )}
              >
                <GitPullRequest className="w-4 h-4 mt-0.5 shrink-0 text-slate-500 dark:text-slate-400" />
                <span>
                  <span className="block text-sm font-medium text-slate-900 dark:text-slate-100">
                    Propose via pull request
                  </span>
                  <span className="block text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                    Commits to a branch for review; the Knowledge Base updates when the PR merges.
                  </span>
                </span>
              </button>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Repository</label>
              <input
                type="text"
                value={repo}
                onChange={(e) => setRepo(e.target.value)}
                placeholder="owner/repo"
                className="w-full px-3 py-2 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
              />
              {!defaultRepo && (
                <p className="mt-1.5 text-xs text-slate-500 dark:text-slate-400">
                  No dedicated repository is configured for this pillar
                  {configureHref ? (
                    <>
                      {' — '}
                      <a href={configureHref} className="text-blue-600 dark:text-blue-400 hover:underline">
                        set one in Settings
                      </a>{' '}
                      so this is pre-filled next time.
                    </>
                  ) : (
                    '.'
                  )}
                </p>
              )}
            </div>

            {mode === 'propose' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Branch</label>
                  <div className="flex items-center gap-2">
                    <GitBranch className="w-4 h-4 text-slate-400" />
                    <input
                      type="text"
                      value={branch}
                      onChange={(e) => setBranch(e.target.value)}
                      className="flex-1 px-3 py-2 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
                    />
                  </div>
                </div>
                <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
                  <input
                    type="checkbox"
                    checked={createPr}
                    onChange={(e) => setCreatePr(e.target.checked)}
                    className="rounded"
                  />
                  Create pull request
                </label>
              </>
            )}

            {/* Sanitize preview (R5) — the pipeline redacts at build either way. */}
            <div className="rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-2.5">
              <div className="flex items-center gap-2 text-xs">
                <ShieldCheck className={cn('w-3.5 h-3.5 shrink-0', redactions > 0 ? 'text-amber-500' : 'text-emerald-500')} />
                {scanState === 'loading' ? (
                  <span className="text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
                    <Spinner size="sm" /> Scanning for sensitive content…
                  </span>
                ) : scanState === 'unavailable' ? (
                  <span className="text-slate-500 dark:text-slate-400">
                    Sanitization runs automatically at publish — preview unavailable here.
                  </span>
                ) : redactions === 0 && reported === 0 ? (
                  <span className="text-slate-600 dark:text-slate-300">Nothing to redact — the document publishes as written.</span>
                ) : (
                  <span className="text-slate-700 dark:text-slate-200">
                    {redactions > 0 && `${redactions} redaction${redactions === 1 ? '' : 's'} will apply on the published pages`}
                    {redactions > 0 && reported > 0 && ' · '}
                    {reported > 0 && `${reported} finding${reported === 1 ? '' : 's'} reported`}
                    {(scan?.findings.length ?? 0) > 0 && (
                      <button
                        onClick={() => setShowFindings((v) => !v)}
                        className="ml-2 text-blue-600 dark:text-blue-400 hover:underline"
                      >
                        {showFindings ? 'Hide findings' : 'Review findings'}
                      </button>
                    )}
                  </span>
                )}
              </div>
              {showFindings && scan && scan.findings.length > 0 && (
                <ul className="mt-2 space-y-1 max-h-32 overflow-y-auto">
                  {scan.findings.map((f, i) => (
                    <li key={i} className="text-[11px] font-mono text-slate-500 dark:text-slate-400 truncate">
                      L{f.line} · {f.ruleId} · {f.action} · "{f.preview}"
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <button
              onClick={handlePublish}
              disabled={publishing || !repo}
              className="w-full px-3 py-2 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              {publishing
                ? 'Publishing...'
                : mode === 'publish' ? 'Publish to Knowledge Base' : 'Open pull request'}
            </button>

            {detail?.publishedAt && (
              <button
                onClick={handleUnpublish}
                disabled={unpublishing}
                className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50"
              >
                {unpublishing ? <Spinner size="sm" tone="current" /> : <Trash2 className="w-3.5 h-3.5" />}
                {unpublishing ? 'Unpublishing…' : 'Unpublish from the Knowledge Base'}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
